import { useEffect, useRef } from 'react';
import { TAU, rand, canvasOf, makeGlow, makeNoise2D } from './meadow/sprites';

// Starry Sky: dark mode background for the Sky of Thoughts (v2).
// A live version of the old night photo: a deep, dark sky packed with fine
// star dust, a faint Milky Way running across it with darker dust lanes, a
// layer of gently twinkling stars and the odd shooting star.

type Star = { x: number; y: number; r: number; peak: number; period: number; phase: number; col: string };

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

export default function StarrySky({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const starGlow = makeGlow('220, 232, 255');
    const sky = document.createElement('canvas');

    let W = 0, H = 0, dpr = 1, t = 0, raf = 0, stars: Star[] = [];
    let shoot: { x: number; y: number; vx: number; vy: number; life: number } | null = null;
    const pointer = { x: 0, y: 0, last: -1e9 };

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      W = canvas.clientWidth || window.innerWidth;
      H = canvas.clientHeight || window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      const mobile = W < 768;
      const SW = W + 40, SH = H + 40; // a little extra for parallax

      sky.width = SW * dpr;
      sky.height = SH * dpr;
      const s = sky.getContext('2d')!;
      s.setTransform(dpr, 0, 0, dpr, 0, 0);
      const g = s.createLinearGradient(0, 0, 0, SH);
      g.addColorStop(0, '#04060b');
      g.addColorStop(0.5, '#070b14');
      g.addColorStop(1, '#0a0f1b');
      s.fillStyle = g;
      s.fillRect(0, 0, SW, SH);

      // The Milky Way: a soft band of light with darker dust lanes, drawn at
      // low resolution and scaled up so it stays smooth.
      const fbm = makeNoise2D();
      const lanes = makeNoise2D();
      const LR = 5, lw = Math.ceil(SW / LR), lh = Math.ceil(SH / LR);
      const band = canvasOf(lw, lh);
      const bctx = band.getContext('2d')!;
      const img = bctx.createImageData(lw, lh);
      const tilt = -0.12;
      const centre = (x: number) => SH * 0.52 + (x - SW / 2) * tilt;
      for (let y = 0; y < lh; y++) {
        for (let x = 0; x < lw; x++) {
          const X = x * LR, Y = y * LR;
          const d = (Y - centre(X)) / (SH * 0.2);
          const core = Math.exp(-d * d);
          const cloud = fbm(X / 180, Y / 180);
          const dust = clamp01((lanes(X / 120, Y / 90, 4) - 0.45) * 3);
          const a = clamp01(core * (0.35 + cloud * 0.9) - dust * core * 0.55);
          const i = (y * lw + x) * 4;
          img.data[i] = 150; img.data[i + 1] = 170; img.data[i + 2] = 210;
          img.data[i + 3] = Math.round(a * 60);
        }
      }
      bctx.putImageData(img, 0, 0);
      s.imageSmoothingQuality = 'high';
      s.drawImage(band, 0, 0, SW, SH);

      // Fine star dust, thicker inside the band.
      const count = mobile ? 2600 : 6000;
      for (let i = 0; i < count; i++) {
        const X = Math.random() * SW;
        let Y = Math.random() * SH;
        if (Math.random() < 0.45) Y = centre(X) + (Math.random() + Math.random() + Math.random() - 1.5) * SH * 0.22;
        const bright = Math.pow(Math.random(), 3);
        s.fillStyle = `rgba(${215 + Math.random() * 40}, ${225 + Math.random() * 30}, 255, ${0.15 + bright * 0.75})`;
        const r = 0.3 + bright * 0.9;
        s.beginPath();
        s.arc(X, Y, r, 0, TAU);
        s.fill();
      }

      // Twinkling stars, same gentle rhythm as the Sky of Thoughts today.
      const cols = ['235, 242, 255', '205, 222, 255', '255, 244, 225'];
      stars = Array.from({ length: mobile ? 90 : 170 }, () => ({
        x: Math.random() * SW, y: Math.random() * SH, r: rand(0.5, 1.4),
        peak: rand(0.4, 1), period: rand(2, 5), phase: Math.random() * TAU,
        col: cols[Math.random() * cols.length | 0],
      }));
    };

    const frame = () => {
      if (!sky.width || !sky.height) return;
      t += 1 / 60;
      const active = t - pointer.last < 4;
      const ox = -20 - (active ? (pointer.x / W - 0.5) * 16 : 0);
      const oy = -20 - (active ? (pointer.y / H - 0.5) * 10 : 0);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(sky, ox * dpr, oy * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.globalCompositeOperation = 'lighter';
      for (const s of stars) {
        const w = 0.5 - 0.5 * Math.cos((t / s.period) * TAU + s.phase);
        const a = 0.2 + (s.peak - 0.2) * w;
        const r = s.r * (0.8 + 0.4 * w);
        const x = s.x + ox, y = s.y + oy;
        ctx.globalAlpha = a * 0.5;
        ctx.drawImage(starGlow, x - r * 3.5, y - r * 3.5, r * 7, r * 7);
        ctx.globalAlpha = 1;
        ctx.fillStyle = `rgba(${s.col}, ${a})`;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fill();
      }

      if (!shoot && Math.random() < 0.0012) {
        shoot = { x: rand(0.1, 0.7) * W, y: rand(0.03, 0.35) * H, vx: rand(7, 10), vy: rand(2, 3.5), life: 1 };
      }
      if (shoot) {
        shoot.x += shoot.vx; shoot.y += shoot.vy; shoot.life -= 0.02;
        const g = ctx.createLinearGradient(shoot.x, shoot.y, shoot.x - shoot.vx * 14, shoot.y - shoot.vy * 14);
        g.addColorStop(0, `rgba(255,255,255,${Math.max(0, shoot.life)})`);
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.moveTo(shoot.x, shoot.y);
        ctx.lineTo(shoot.x - shoot.vx * 14, shoot.y - shoot.vy * 14);
        ctx.stroke();
        if (shoot.life <= 0) shoot = null;
      }
      ctx.globalCompositeOperation = 'source-over';
    };

    const loop = () => {
      frame();
      raf = requestAnimationFrame(loop);
    };
    const start = () => { if (!raf && !reduced) raf = requestAnimationFrame(loop); };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      pointer.x = e.clientX - r.left;
      pointer.y = e.clientY - r.top;
      pointer.last = t;
    };
    const onVis = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => { build(); frame(); }, 150);
    };

    build();
    frame();
    start();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      clearTimeout(resizeTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={`block w-full h-full pointer-events-none ${className}`} />;
}
